import * as React from 'react';

import Tooltip from 'components/Tooltip';
import SVGIcon from 'components/svg-icon/SVGIcon';
import ProposalMetric from '../metric/ProposalMetric';

import { abbrNum } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';
import { getFormattedPercentageRate } from 'utils/formatters';

import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';
import type { ProposalCreatorProfile } from 'types';

import './proposal-tile-simple-info.scss';

applyTranslations(require('./proposal-tile-simple-info.i18n.json'));

type Props = {
  channelType: ProposalTileChannelType,
  creatorProfile: ProposalCreatorProfile
};

const IconMapping: Record<ProposalTileChannelType, string> = {
  instagram: 'instagram-no-customisation',
  tiktok: 'tiktok-no-customisation'
};

function prepareMetrics(
  channelType: ProposalTileChannelType,
  creatorProfile: ProposalCreatorProfile
): Array<Pick<React.ComponentProps<typeof ProposalMetric>, 'label' | 'value' | 'tooltip'>> {
  const channelInfo = creatorProfile.channels[channelType];

  if (channelType === 'instagram') {
    return [{
      label: __t('proposal_tile_simple_info.followers_label'),
      value: abbrNum(channelInfo?.metrics?.followers)
    }, {
      label: __t('proposal_tile_simple_info.engagement_rate_label'),
      value: getFormattedPercentageRate(channelInfo?.metrics?.engagement)
    }];
  }

  if (channelType === 'tiktok') {
    return [
      {
        label: __t('proposal_tile_simple_info.followers_label'),
        value: abbrNum(channelInfo?.metrics?.followers)
      },
      {
        label: __t('proposal_tile_simple_info.median_views_label'),
        value: abbrNum(channelInfo?.metrics?.tiktokMedianViews),
        tooltip: __t('proposal_tile_simple_info.median_views_tooltip')
      }
    ];
  }

  return [];
}

const InfoSimple: React.FunctionComponent<Props> = (
  {
    channelType,
    creatorProfile
  }: Props
): JSX.Element => {
  const channelInfo = creatorProfile.channels[channelType];

  return (
    <section className="proposal-tile-simple-info">
      <div className="proposal-tile-simple-info__channel">
        <Tooltip
          message={__t(`proposal_tile_simple_info.view_channel_tooltips.${channelType}`)}
          classNameWrapper="proposal-tile-simple-info__channel-icon"
        >
          <SVGIcon
            name={IconMapping[channelType]}
            width={32}
            height={32}
          />
        </Tooltip>

        <Tooltip
          message={__t(`proposal_tile_simple_info.view_channel_tooltips.${channelType}`)}
          classNameWrapper="text-overflow-ellipsis"
        >
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={channelInfo?.url}
            className="proposal-tile-simple-info__channel-name text-overflow-ellipsis"
          >
            @{channelInfo?.username}
          </a>
        </Tooltip>
      </div>
      <div className="proposal-tile-simple-info__metrics">
        {
          prepareMetrics(channelType, creatorProfile)
            .filter(metric => Boolean(metric.value))
            .map((metric) => (
              <div className="proposal-tile-simple-info__metric" key={metric.label}>
                <ProposalMetric
                  type="basic"
                  label={metric.label}
                  value={metric.value}
                  tooltip={metric.tooltip}
                />
              </div>
            ))
        }
      </div>
    </section>
  );
};

export default InfoSimple;
